"use server";

import { encodedRedirect } from "@/utils/utils";
import { createClient } from "@/utils/supabase/server";
import { headers } from "next/headers";
import { redirect } from "next/navigation";
import { logAuthSecurityEvent } from "@/lib/security-events";

/**
 * Type de retour standard pour toutes les actions d'authentification
 */
export type AuthActionResponse = {
  error?: string;
  success?: boolean | string;
  data?: any;
};

/**
 * Inscription d'un utilisateur
 */
export const signUpAction = async (formData: FormData): Promise<AuthActionResponse> => {
  const email = formData.get("email")?.toString();
  const password = formData.get("password")?.toString();
  const supabase = await createClient();
  const origin = (await headers()).get("origin");

  if (!email || !password) {
    return encodedRedirect(
      "error",
      "/register",
      "Email and password are required"
    );
  }

  const { error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: `${origin}/api/auth/callback?redirect_to=/register`,
    },
  });

  if (error) {
    console.error(error.code, error.message);
    return encodedRedirect("error", "/register", error.message);
  }

  return encodedRedirect(
    "success",
    "/register",
    "Thanks for signing up! Please check your email for a verification link."
  );
};

/**
 * Connexion par email + mot de passe
 */
export const signInAction = async (formData: FormData): Promise<AuthActionResponse> => {
  const email = formData.get("email")?.toString();
  const password = formData.get("password")?.toString();
  const supabase = await createClient();

  if (!email || !password) {
    return encodedRedirect("error", "/login", "Email and password are required");
  }

  const { error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    return encodedRedirect("error", "/login", error.message);
  }

  return redirect("/feed");
};

/**
 * Déconnexion de l'utilisateur (reste sur la même page)
 */
export const signOutAction = async (formData?: FormData): Promise<AuthActionResponse> => {
  const supabase = await createClient();
  const { data: { user } } = await supabase.auth.getUser();
  const userId = user?.id ?? null;

  if (userId) {
    await logAuthSecurityEvent({
      event_type: "auth.logout",
      user_id: userId,
      success: true,
      formData: formData ?? new FormData(),
    });
  }

  await supabase.auth.signOut();
  return { success: true };
};
